import React, { useState } from 'react';
import { ShoppingCart, Loader, Lock, CreditCard } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PPVPurchaseButton = ({ content, contentType, onPurchaseComplete }) => {
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);

  const price = content?.ppv_price || content?.price || 4.99;

  const handlePurchase = async () => {
    if (!isAuthenticated) {
      alert('Please sign in to purchase');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/ppv/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content_id: content.id,
          content_type: contentType,
          origin_url: window.location.origin,
        }),
      });

      const data = await response.json();

      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Purchase error:', error);
      alert('Failed to start purchase. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handlePurchase}
      disabled={loading}
      className="flex items-center gap-2 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black font-bold px-6 py-3 rounded-lg transition-all disabled:opacity-50"
      data-testid="ppv-purchase-btn"
    >
      {loading ? (
        <>
          <Loader size={20} className="animate-spin" />
          Processing...
        </>
      ) : (
        <>
          <ShoppingCart size={20} />
          Buy Now ${price.toFixed(2)}
        </>
      )}
    </button>
  );
};

const PPVContentCard = ({ content, contentType, onPlay }) => {
  const isPurchased = content?.is_purchased || false;
  const price = content?.ppv_price || content?.price || 4.99;

  return (
    <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
      <div className="flex items-start gap-4">
        <img
          src={content.poster || content.thumbnail}
          alt={content.title}
          className="w-32 h-48 object-cover rounded-lg"
        />
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="bg-yellow-500 text-black text-xs font-bold px-2 py-1 rounded">
              PPV
            </span>
            <span className="text-yellow-500 font-bold">${price.toFixed(2)}</span>
          </div>
          <h3 className="text-xl font-bold text-white mb-2">{content.title}</h3>
          <p className="text-gray-400 text-sm mb-4 line-clamp-2">{content.description}</p>
          
          {isPurchased ? (
            <button
              onClick={() => onPlay && onPlay(content)}
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
            >
              Play Now
            </button>
          ) : (
            <div className="flex items-center gap-3">
              <PPVPurchaseButton content={content} contentType={contentType} />
              <div className="flex items-center gap-1 text-gray-500 text-sm">
                <Lock size={14} />
                Premium Content
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { PPVPurchaseButton, PPVContentCard };
export default PPVPurchaseButton;
